import LZString from 'lz-string';
import { ShareData } from './FileService';

/**
 * Service for handling sharing operations
 */
export class SharingService {
  /**
   * Extracts share data from URL parameters
   * @returns The share data parameter if present, null otherwise
   */
  static getShareDataFromUrl(): string | null {
    const params = new URLSearchParams(window.location.search);
    return params.get('data');
  }

  /**
   * Converts HTML to plain text while preserving line breaks
   * @param html - The HTML string to convert
   * @returns Plain text with preserved line breaks
   */
  private static htmlToPlainTextWithLineBreaks(html: string): string {
    if (!html) return '';
    
    // The HTML structure from contentEditable creates this pattern:
    // "text <a>[0:00]</a>&nbsp;<div><a>[0:00]</a>&nbsp;text 2<br></div>"
    // We need to treat the <div> as a line break separator
    
    // Step 1: Insert line breaks before <div> tags (new line starts)
    let processedHtml = html.replace(/<div[^>]*>/gi, '\n');
    
    // Step 2: Convert </div> tags to nothing (already handled by the opening tag)
    processedHtml = processedHtml.replace(/<\/div>/gi, '');
    
    // Step 3: Convert <br> tags to newlines
    processedHtml = processedHtml.replace(/<br\s*\/?>/gi, '\n');
    
    // Step 4: Handle other block elements
    processedHtml = processedHtml.replace(/<\/p>/gi, '\n');
    processedHtml = processedHtml.replace(/<\/li>/gi, '\n');
    
    // Step 5: Strip all remaining HTML tags
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = processedHtml;
    let plainText = tempDiv.textContent || tempDiv.innerText || '';
    
    // Step 6: Clean up the result
    const lines = plainText.split('\n');
    const cleanedLines = [];
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      // Skip completely empty lines, but preserve the structure
      if (line.length > 0) {
        cleanedLines.push(line);
      } else if (cleanedLines.length > 0 && i < lines.length - 1) {
        // Keep empty lines in the middle, but not at start/end
        const nextNonEmptyIndex = lines.slice(i + 1).findIndex(l => l.trim().length > 0);
        if (nextNonEmptyIndex >= 0) {
          cleanedLines.push(''); // Preserve empty line
        }
      }
    }
    
    return cleanedLines.join('\n');
  }

  /**
   * Generates simple share text with just intro and URL
   * @param shareUrl - The share URL to include
   * @returns Simple formatted share text
   */
  static generateSimpleShareText(shareUrl: string): string {
    return `Here's a shared audio setup from ABmyMix:\n${shareUrl}`;
  }

  /**
   * Generates enhanced share text with track names and notes
   * @param shareData - The share data containing tracks and notes
   * @param shareUrl - The share URL to include
   * @returns Formatted share text with track details and notes
   */
  static generateEnhancedShareText(shareData: ShareData, shareUrl: string): string {
    let shareText = "Here's a shared audio setup from ABmyMix:\n";
    
    // Include custom title if it exists
    if (shareData.pageTitle && shareData.pageTitle.trim()) {
      shareText += `\n${shareData.pageTitle.trim()}\n\n`;
    }
    
    shareText += shareUrl + "\n\n";
    
    if (shareData.tracks && shareData.tracks.length > 0) {
      shareText += "Audio Files:\n\n";
      
      shareData.tracks.forEach((track, index) => {
        // Use custom display name if available, otherwise fall back to track name
        const customDisplayName = shareData.displayNames?.[track.id.toString()];
        let trackName = customDisplayName || track.name || `Track ${track.id}`;
        // Restore / characters in file names for user-facing text (they may have been replaced with : for backend storage)
        trackName = trackName.replace(/:/g, '/');
        shareText += `${index + 1}: ${trackName}\n`;
        
        // Add notes for this track if they exist
        const trackNotes = shareData.notes && shareData.notes[track.id.toString()];
        if (trackNotes && trackNotes.trim()) {
          // Convert HTML to plain text while preserving line breaks
          const plainNotes = this.htmlToPlainTextWithLineBreaks(trackNotes);
          
          if (plainNotes.trim()) {
            // Add each line of notes with space indentation (more mobile-friendly than tabs)
            const noteLines = plainNotes.split('\n');
            noteLines.forEach(line => {
              if (line.trim()) {
                shareText += `    ${line.trim()}\n`; // 4 spaces instead of tab
              }
            });
          }
        }
        shareText += "\n";
      });
      
      // Add total file size if available
      if (shareData.totalFileSize && shareData.totalFileSize > 0) {
        const sizeMB = Math.round(shareData.totalFileSize / (1024 * 1024));
        shareText += `Total size of all files: ~${sizeMB}MB\n`;
      }
    }
    
    return shareText.trim();
  }

  /**
   * Compresses and encodes share data for URL sharing
   * @param shareData - The data to be shared
   * @returns Compressed and encoded string for URL
   */
  static compressShareData(shareData: ShareData): string {
    return LZString.compressToEncodedURIComponent(JSON.stringify(shareData));
  }

  /**
   * Creates a shareable URL with the given data
   * @param shareData - The data to be shared
   * @returns Complete URL with share data
   */
  static createShareUrl(shareData: ShareData): string {
    const compressedData = this.compressShareData(shareData);
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.set('data', compressedData);
    return currentUrl.toString();
  }

  /**
   * Validates share data structure
   * @param data - The share data to validate
   * @returns true if valid, false otherwise
   */
  static isValidShareData(data: unknown): data is ShareData {
    if (!data || typeof data !== 'object') return false;
    const shareData = data as ShareData;
    return (
      Array.isArray(shareData.tracks) &&
      typeof shareData.notes === 'object' &&
      typeof shareData.timestamp === 'number' &&
      typeof shareData.isOwner === 'boolean' &&
      typeof shareData.allowDownloads === 'boolean' &&
      Array.isArray(shareData.originalUrls) &&
      (shareData.pageTitle === undefined || typeof shareData.pageTitle === 'string') &&
      (shareData.totalFileSize === undefined || typeof shareData.totalFileSize === 'number')
    );
  }
} 